/**
 * Session context — wraps the app and provides timecore auth state.
 *
 * Usage:
 *   wrap root with <SessionProvider>
 *   read auth state anywhere with useSession()
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { authApi, orgApi, type TimecoreUser } from './api';
import { getDdpClient } from './ddp';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SessionState {
  user: TimecoreUser | null;
  loading: boolean;
  /** True when the user is authenticated but has not yet claimed a username. */
  needsUsernameClaim: boolean;
  /** Block message if user is blocked from all orgs. */
  blockMessage?: string | null;
  /** Re-fetch session from timecore — call after sign-in / sign-up. */
  refetch: () => Promise<void>;
  /** Sign out from timecore and clear local session state. */
  signOut: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const SessionContext = createContext<SessionState>({
  user: null,
  loading: true,
  needsUsernameClaim: false,
  blockMessage: null,
  refetch: async () => {},
  signOut: async () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<TimecoreUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [blockMessage, setBlockMessage] = useState<string | null>(null);

  const fetchSession = useCallback(async () => {
    setLoading(true);
    setBlockMessage(null);
    console.log('[TimeHuddle] fetchSession: calling getMe...');
    const t = performance.now();
    try {
      const ddp = getDdpClient();
      const meteorUser = await ddp.getCurrentUser();

      if (meteorUser) {
        console.log(
          `[TimeHuddle] fetchSession: getMe resolved in ${(performance.now() - t).toFixed(0)}ms — user=${meteorUser.email}`,
        );

        // Fetch organizations for the user
        let organizations: Array<{
          id: string;
          name: string;
          slug: string;
          enterpriseId: string | null;
          role: 'owner' | 'admin' | 'member';
          allowAutoJoin: boolean;
        }> = [];
        try {
          console.log('[TimeHuddle] fetchSession: calling orgApi.listOrganizations()...');
          const orgs = await orgApi.listOrganizations();
          // Filter out orgs where role is null
          organizations = orgs.filter(
            (o): o is typeof o & { role: 'owner' | 'admin' | 'member' } => o.role !== null,
          );
          console.log(
            `[TimeHuddle] fetchSession: loaded ${organizations.length} organizations`,
            organizations,
          );
        } catch (err) {
          console.error(
            '[TimeHuddle] fetchSession: failed to load organizations:',
            err instanceof Error ? err.message : String(err),
            err,
          );
        }

        setUser({
          id: meteorUser.id,
          email: meteorUser.email,
          name: meteorUser.name,
          createdAt: new Date().toISOString(),
          emailVerified: meteorUser.emailVerified ?? true,
          image: meteorUser.image ?? null,
          backgroundUrl: null,
          username: meteorUser.username ?? null,
          organizationMembership: null,
          organizations,
        });
      } else {
        console.log(
          `[TimeHuddle] fetchSession: getMe resolved in ${(performance.now() - t).toFixed(0)}ms — no user found`,
        );
        setUser(null);
      }
    } catch (err) {
      console.log(
        `[TimeHuddle] fetchSession: getMe failed in ${(performance.now() - t).toFixed(0)}ms — ${String(err)}`,
      );
      // Check if it's a blocking error
      const errMessage = err instanceof Error ? err.message : String(err);
      if (errMessage.includes('suspended') || errMessage.includes('blocked')) {
        setBlockMessage(errMessage);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // The first fetchSession gives up if the backend is down (e.g. mid-restart),
  // leaving the tab logged out. Re-run it once DDP recovers so the session
  // restores itself without a manual reload.
  useEffect(() => getDdpClient().onReconnect(() => void fetchSession()), [fetchSession]);

  // Native OAuth deep-link login: after the timehuddle://auth deep link stores
  // the resume token, log the DDP connection in with it and refetch. A plain
  // re-render won't re-authenticate the already-connected DDP socket.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { resumeToken?: string } | undefined;
      void (async () => {
        const ddp = getDdpClient();
        if (detail?.resumeToken) {
          await ddp.loginWithMeteorToken('', detail.resumeToken).catch(() => {});
        }
        await fetchSession();
      })();
    };
    window.addEventListener('timehuddle:oauth-login', handler);
    return () => window.removeEventListener('timehuddle:oauth-login', handler);
  }, [fetchSession]);

  const signOut = useCallback(async () => {
    // Clear token FIRST so no wormhole calls fire with invalidated token
    localStorage.removeItem('meteor_resume_token');
    // Clear user state immediately to stop any reactive refetches
    setUser(null);
    // Navigate to root to show landing/login page
    if (window.location.pathname !== '/') {
      window.history.pushState(null, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
    // Then invalidate server-side session
    const ddp = getDdpClient();
    await ddp.logout().catch(() => {});
    await authApi.signOut().catch(() => {});
  }, []);

  const needsUsernameClaim = !!user && user.username === null;

  return (
    <SessionContext.Provider
      value={{ user, loading, needsUsernameClaim, blockMessage, refetch: fetchSession, signOut }}
    >
      {children}
    </SessionContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useSession = (): SessionState => useContext(SessionContext);
